import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Lock, Server, Eye } from 'lucide-react'
import { APP_NAME } from '@/shared/lib/constants'

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-legal-50 dark:bg-legal-900 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <article className="bg-white dark:bg-legal-800 rounded-xl shadow-md p-8">
          <h1 className="text-3xl font-bold text-legal-900 dark:text-white mb-6">
            Política de Privacidad y Protección de Datos
          </h1>

          <p className="text-sm text-legal-600 dark:text-legal-400 mb-8">
            Última actualización: 11 de noviembre de 2025
          </p>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-600 p-4 mb-8">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  Compromiso con tu Privacidad
                </p>
                <p className="text-green-800 dark:text-green-200 text-sm">
                  {APP_NAME} opera bajo el principio de "cero almacenamiento de datos sensibles".
                  No guardamos, registramos ni almacenamos ningún documento o información personal de los PDFs que verificás.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 text-legal-700 dark:text-legal-300">
            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                1. Información que NO recopilamos
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-legal-50 dark:bg-legal-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Server className="w-5 h-5 text-legal-600 dark:text-legal-400" />
                    <h3 className="font-semibold text-legal-900 dark:text-white">Documentos PDF</h3>
                  </div>
                  <p className="text-sm">
                    Los archivos se procesan en memoria y se eliminan inmediatamente después del análisis.
                    NO se guardan copias.
                  </p>
                </div>

                <div className="bg-legal-50 dark:bg-legal-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-5 h-5 text-legal-600 dark:text-legal-400" />
                    <h3 className="font-semibold text-legal-900 dark:text-white">Contenido de PDFs</h3>
                  </div>
                  <p className="text-sm">
                    NO leemos, almacenamos ni registramos el contenido textual o imágenes de tus documentos.
                  </p>
                </div>

                <div className="bg-legal-50 dark:bg-legal-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-5 h-5 text-legal-600 dark:text-legal-400" />
                    <h3 className="font-semibold text-legal-900 dark:text-white">Datos Personales</h3>
                  </div>
                  <p className="text-sm">
                    No solicitamos, recopilamos ni almacenamos información personal identificable.
                  </p>
                </div>

                <div className="bg-legal-50 dark:bg-legal-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-legal-600 dark:text-legal-400" />
                    <h3 className="font-semibold text-legal-900 dark:text-white">Metadatos</h3>
                  </div>
                  <p className="text-sm">
                    No almacenamos metadata de los PDFs analizados más allá del tiempo necesario para el procesamiento.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                2. Cómo funciona el procesamiento
              </h2>
              <ol className="space-y-3">
                <li className="flex gap-3">
                  <span className="font-semibold text-legal-900 dark:text-white">1.</span>
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Cálculo de hash local</p>
                    <p>El hash SHA-256 del documento se calcula en tu navegador (cliente) antes de enviar el archivo.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="font-semibold text-legal-900 dark:text-white">2.</span>
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Procesamiento en memoria</p>
                    <p>El PDF se procesa únicamente en la memoria RAM del servidor, sin escritura a disco.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="font-semibold text-legal-900 dark:text-white">3.</span>
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Análisis técnico</p>
                    <p>Se verifica la firma digital, certificado PKI, y validez según Ley 25.506.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="font-semibold text-legal-900 dark:text-white">4.</span>
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Eliminación inmediata</p>
                    <p>El archivo se elimina de memoria inmediatamente después de generar el resultado.</p>
                  </div>
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                3. Datos técnicos que sí recopilamos
              </h2>
              <p className="mb-2">Para el funcionamiento básico de la plataforma, recopilamos únicamente:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Dirección IP (anonimizada, solo para prevención de abuso)</li>
                <li>Información del navegador (user-agent) para compatibilidad técnica</li>
                <li>Estadísticas anónimas de uso (número de verificaciones, sin identificadores)</li>
              </ul>
              <p className="mt-3 text-sm">
                Estos datos NO se vinculan con los documentos procesados ni permiten identificar usuarios o documentos específicos.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                4. Cookies y Almacenamiento Local
              </h2>
              <p>
                {APP_NAME} utiliza únicamente almacenamiento local del navegador (localStorage) para:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Preferencias de tema (modo oscuro/claro)</li>
                <li>Configuración de interfaz de usuario</li>
              </ul>
              <p className="mt-2">
                NO utilizamos cookies de terceros ni herramientas de tracking.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                5. Cumplimiento Normativo
              </h2>
              <p className="mb-2">
                {APP_NAME} cumple con:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Ley 25.326 de Protección de Datos Personales (Argentina)</li>
                <li>Disposición 11/2006 de la DNPDP (Dirección Nacional de Protección de Datos Personales)</li>
                <li>Principios de Privacy by Design</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                6. Seguridad
              </h2>
              <p>
                Implementamos medidas técnicas y organizativas para proteger la confidencialidad de los datos procesados:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Conexiones HTTPS cifradas (TLS 1.3)</li>
                <li>Procesamiento en memoria sin persistencia</li>
                <li>No hay bases de datos que puedan ser comprometidas</li>
                <li>Infraestructura serverless con aislamiento por request</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                7. Derechos del Usuario
              </h2>
              <p className="mb-2">
                Dado que NO almacenamos datos personales ni documentos:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>No hay datos para solicitar acceso o rectificación</li>
                <li>No hay datos para solicitar eliminación</li>
                <li>Tu información está protegida por diseño (data minimization)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                8. Cambios a esta Política
              </h2>
              <p>
                Nos reservamos el derecho de actualizar esta política. Los cambios se publicarán en esta página
                con fecha de actualización. El uso continuado de {APP_NAME} constituye aceptación de la política vigente.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-legal-900 dark:text-white mb-3">
                9. Contacto
              </h2>
              <p>
                Para consultas sobre privacidad y protección de datos, contactá a través de{' '}
                <a
                  href="https://arteclaw.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 underline"
                >
                  Arteclaw
                </a>.
              </p>
            </section>
          </div>
        </article>
      </div>
    </div>
  )
}
